// app/src/App.tsx
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SessionProvider, useSession } from "./lib/session";
import { NavProvider } from "./lib/nav";
import { TextProvider } from "./lib/text";
import SmartInput from "./lib/smartInput";
import Login from "./modules/auth/Login";
const Terminal = lazy(() => import("./modules/pos/Terminal"));
const Dashboard = lazy(() => import("./modules/backoffice/Dashboard"));
const Transactions = lazy(() => import("./modules/backoffice/Transactions"));
const Workshop = lazy(() => import("./modules/workshop/Workshop"));
const JobCard = lazy(() => import("./modules/workshop/JobCard"));
const Reminders = lazy(() => import("./modules/workshop/Reminders"));
const Diary = lazy(() => import("./modules/workshop/Diary"));
const Staff = lazy(() => import("./modules/admin/Staff"));
const Settings = lazy(() => import("./modules/admin/Settings"));
const Invoice = lazy(() => import("./modules/workshop/Invoice"));
const VehicleHistory = lazy(() => import("./modules/workshop/VehicleHistory"));
const Inspection = lazy(() => import("./modules/workshop/Inspection"));
const BayBoard = lazy(() => import("./modules/workshop/BayBoard"));
const Purchasing = lazy(() => import("./modules/purchasing/Purchasing"));
const Suppliers = lazy(() => import("./modules/purchasing/Suppliers"));
const Expenses = lazy(() => import("./modules/backoffice/Expenses"));
const Bank = lazy(() => import("./modules/backoffice/Bank"));
const Mot = lazy(() => import("./modules/backoffice/Mot"));
const Kits = lazy(() => import("./modules/parts/Kits"));
const Accounts = lazy(() => import("./modules/accounts/Accounts"));
const Export = lazy(() => import("./modules/admin/Export"));
const Import = lazy(() => import("./modules/admin/Import"));
const Audit = lazy(() => import("./modules/admin/Audit"));
const SystemAudit = lazy(() => import("./modules/backoffice/SystemAudit"));
const StockCount = lazy(() => import("./modules/records/StockCount"));
const Garage = lazy(() => import("./modules/customers/Garage"));
const Customers = lazy(() => import("./modules/customers/Customers"));
const Reports = lazy(() => import("./modules/backoffice/Reports"));
const Glossary = lazy(() => import("./modules/backoffice/Glossary"));
const WorkshopFinance = lazy(() => import("./modules/backoffice/WorkshopFinance"));
const Orders = lazy(() => import("./modules/backoffice/Orders"));
const Logs = lazy(() => import("./modules/backoffice/Logs"));
const Shifts = lazy(() => import("./modules/backoffice/Shifts"));
const Navigation = lazy(() => import("./modules/admin/Navigation"));
const Wording = lazy(() => import("./modules/admin/Wording"));
const QrLabels = lazy(() => import("./modules/catalogue/QrLabels"));
const Waste = lazy(() => import("./modules/admin/Waste"));
const StockIncidents = lazy(() => import("./modules/admin/StockIncidents"));
const WebsiteAdmin = lazy(() => import("./modules/website/WebsiteAdmin"));
const Bikes = lazy(() => import("./modules/bikes/Bikes"));
const Payments = lazy(() => import("./modules/payments/Payments"));
const Catalogue = lazy(() => import("./modules/catalogue/Catalogue"));
const Records = lazy(() => import("./modules/records/Records"));
const PublicSite = lazy(() => import("./modules/public/PublicSite"));
const WhatsAppAdmin = lazy(() => import("./modules/admin/WhatsApp"));
import { UiHost } from "./modules/ui/Dialog";
import Layout from "./modules/ui/Layout";
import BottomNav from "./modules/ui/BottomNav";
import { BRAND_NAME, PUBLIC_HOST_RE, CATALOGUE_HOST_RE } from "./lib/brand";
import { AppErrorBoundary, SystemErrorPage } from "./modules/ui/SystemError";
import BootLoader from "./modules/ui/BootLoader";
import DocumentViewerHost from "./modules/ui/DocumentViewer";
import { TopProgressBar } from "./modules/ui/Loader";

function Guard({ children }: { children: JSX.Element }) {
  const { user, loading } = useSession();
  if (loading) return <FullScreenLoader />;
  return user ? children : <Navigate to="/login" replace />;
}

/** See ui/BootLoader — the old version painted a dark backdrop on a div that
 *  collapsed, then drew the white-ink logo on it, so on a light page it was
 *  white on white and looked like nothing had loaded. */
const FullScreenLoader = BootLoader;

export default function App() {
  // Apex host (or the /site preview path) serves the public marketing site;
  // the EPOS app runs on the login subdomain (and localhost during dev).
  const host = window.location.hostname;
  const isApex = PUBLIC_HOST_RE.test(host);
  const isCatalogue = CATALOGUE_HOST_RE.test(host);
  const isPreview = window.location.pathname === "/site" || window.location.pathname.startsWith("/site/")
    || new URLSearchParams(window.location.search).get("preview") === "site";
  if (isApex || isPreview) return <><TopProgressBar /><UiHost /><Suspense fallback={<FullScreenLoader />}><AppErrorBoundary><PublicSite basename={isApex ? "/" : "/site"} catalogue={isCatalogue} /></AppErrorBoundary></Suspense></>;
  return <><TopProgressBar /><UiHost /><EposApp /></>;
}

/** Keeps the browser-tab title in step with the current admin screen. */
const TITLES: [string, string][] = [
  ["/workshop/diary", "Diary"], ["/workshop/reminders", "Reminders"], ["/workshop", "Workshop"], ["/board", "Bay board"], ["/garage", "Garage"], ["/stock-count", "Stock counts"], ["/system-audit", "System audit"], ["/backoffice", "Dashboard"], ["/reports", "Reports"],
  ["/finance", "Finance"], ["/accounts", "Accounts"], ["/transactions", "Transactions"], ["/payments", "Payments"],
  ["/catalogue", "Catalogue"], ["/kits", "Service kits"], ["/purchasing", "Purchasing"], ["/suppliers", "Suppliers"],
  ["/records", "Stock records"], ["/stock-incidents", "Stock incidents"], ["/bikes", "Bikes"], ["/customers", "Customers"],
  ["/orders", "Web orders"], ["/website", "Website"], ["/whatsapp", "WhatsApp"], ["/staff", "Staff"], ["/settings", "Settings"],
  ["/shifts", "Close shift"], ["/bank", "Bank"], ["/navigation", "Navigation"], ["/wording", "Wording"], ["/qr-labels", "QR labels"], ["/expenses", "Expenses"], ["/mot", "MOT"], ["/waste", "Waste"], ["/audit", "Audit log"], ["/logs", "Logs"], ["/glossary", "Glossary"],
  ["/export", "Export"], ["/import", "Import"], ["/login", "Sign in"],
];
function RouteTitle() {
  const loc = useLocation();
  useEffect(() => {
    const hit = TITLES.find(([p]) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
    document.title = hit ? `${hit[1]} · ${BRAND_NAME}` : loc.pathname === "/" ? `Store · ${BRAND_NAME}` : `${BRAND_NAME} EPOS`;
  }, [loc.pathname]);
  return null;
}

function EposApp() {
  return (
    <SessionProvider>
      <NavProvider>
      <TextProvider>
      <BrowserRouter>
        <AppErrorBoundary>
        <Routes>
          {/* Standalone, full-screen surfaces (no back-office chrome) */}
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Guard><Suspense fallback={<FullScreenLoader />}><Terminal /></Suspense></Guard>} />

          {/* Back Office: everything except the Store renders inside the shared shell
              (header + section tabs + sub-tabs), guarded once. */}
          <Route element={<Guard><Layout /></Guard>}>
            <Route path="/workshop" element={<Workshop />} />
            <Route path="/board" element={<BayBoard />} />
            <Route path="/workshop/reminders" element={<Reminders />} />
            <Route path="/workshop/diary" element={<Diary />} />
            <Route path="/workshop/vehicle/:vid" element={<VehicleHistory />} />
            <Route path="/workshop/:id/invoice" element={<Invoice />} />
            <Route path="/workshop/:id/inspection" element={<Inspection />} />
            <Route path="/workshop/:id" element={<JobCard />} />
            <Route path="/purchasing" element={<Purchasing />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/bank" element={<Bank />} />
            <Route path="/mot" element={<Mot />} />
            <Route path="/records" element={<Records />} />
            <Route path="/kits" element={<Kits />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/export" element={<Export />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/system-audit" element={<SystemAudit />} />
            <Route path="/stock-count" element={<StockCount />} />
            <Route path="/garage" element={<Garage />} />
            <Route path="/import" element={<Import />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/reports" element={<Reports />} />

            <Route path="/glossary" element={<Glossary />} />
            <Route path="/finance" element={<WorkshopFinance />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/waste" element={<Waste />} />
            <Route path="/stock-incidents" element={<StockIncidents />} />
            <Route path="/website" element={<WebsiteAdmin />} />
            <Route path="/whatsapp" element={<WhatsAppAdmin />} />
            <Route path="/bikes" element={<Bikes />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/backoffice" element={<Dashboard />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/shifts" element={<Shifts />} />
            <Route path="/navigation" element={<Navigation />} />
            <Route path="/wording" element={<Wording />} />
            <Route path="/qr-labels" element={<QrLabels />} />
          </Route>

          <Route path="*" element={<SystemErrorPage kind="not_found" />} />
        </Routes>
        </AppErrorBoundary>
        <RouteTitle />
        <BottomNav />
        <DocumentViewerHost />
        <SmartInput />
      </BrowserRouter>
      </TextProvider>
      </NavProvider>
    </SessionProvider>
  );
}
